/**
 * Prompt history for the Fias AI composer — the Up/Down arrow recall of prompts
 * the user has already sent, the way a shell (or Claude Code) recalls commands.
 *
 * WHY THIS IS CLIENT-LOCAL AND NOT A SERVER TABLE
 * -----------------------------------------------
 * Durable, cross-device recall of what the user has said already exists: the
 * History pane reads `GET /v1/arches/fias-ai/conversations`. This module is the
 * fast local shortcut over that same intent — a composer affordance, not a
 * second store of user data. A new per-user persistent table would have to be
 * either inside the Vault substrate or deliberately outside it with a pin test,
 * a single mutation pipeline, and route-layer holder gating (see CLAUDE.md);
 * none of that is warranted for a keystroke buffer, and the operative Vault
 * question — "would the user expect this row in their Vault as data?" — answers
 * itself here.
 *
 * Storage rules, all of which the tests pin:
 *  - keys are namespaced per user AND per composer surface, so two accounts on
 *    one browser never see each other's prompts;
 *  - `persist: false` keeps history in memory for the life of the tab and writes
 *    nothing. Guest sessions use this: a guest's `usr_` id is never revisited
 *    once the `guest-purge` worker hard-deletes the account, so persisting under
 *    it would strand text permanently in what is often a shared browser;
 *  - every storage access is lazy and guarded — unavailable storage (private
 *    mode, SSR, tests), malformed JSON, or a quota failure degrades to the
 *    in-memory mirror and never throws at the caller. A surface whose WRITE
 *    failed then keeps reading from the mirror and stops retrying, so a rejected
 *    write doesn't read back as "no history" or cost a 64 KB serialize per send;
 *  - appends are read-modify-write against storage, never against a cached
 *    array, so two open tabs interleave instead of clobbering each other;
 *  - what comes back OUT of storage is re-validated and re-capped — a
 *    hand-tampered value cannot smuggle a non-string into the textarea or
 *    balloon memory.
 */

/** Most entries retained per surface. Oldest are dropped first. */
export const PROMPT_HISTORY_MAX_ENTRIES = 100;
/** Prompts longer than this are not recorded at all (never truncated — a truncated recall lies). */
export const PROMPT_HISTORY_MAX_ENTRY_CHARS = 8_000;
/** Total character budget per surface, to stay well clear of the origin's storage quota. */
export const PROMPT_HISTORY_MAX_TOTAL_CHARS = 64_000;

const KEY_PREFIX = 'fias-ai-prompt-history';

/** Persisted shape. Versioned so a future change can migrate rather than corrupt. */
interface StoredPromptHistory {
  v: 1;
  /** Newest first. */
  entries: string[];
}

interface PromptHistoryOptions {
  /** false ⇒ in-memory only for the life of the tab. Default true. */
  persist?: boolean;
}

/**
 * In-memory mirror. Used as the sole backend when `persist` is false, and as the
 * fallback whenever `localStorage` is unavailable or throws.
 */
const memoryHistories = new Map<string, string[]>();

/**
 * Surfaces whose persisted write was rejected (quota, storage revoked
 * mid-session). Reads for these come from the mirror instead of storage, which
 * would otherwise report the stale — usually empty — persisted value, and
 * further writes are skipped rather than retried. `clearPromptHistory` lifts the
 * flag, so freeing space and clearing is the way back to persistence.
 */
const degradedKeys = new Set<string>();

/**
 * Storage key for one composer surface and user, or `null` when there is no user
 * — a null key is how callers express "history is off" at the type level rather
 * than by passing an empty string.
 */
export function promptHistoryKey(
  surface: string,
  user: { userId: string } | null | undefined,
): string | null {
  if (!user?.userId) return null;
  return `${KEY_PREFIX}:${user.userId}:${surface}`;
}

/** `localStorage` if it is usable, else null. Never throws (private mode, SSR, sandboxed iframe). */
function getStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Enforce the entry/list caps on an already-sanitized, newest-first list.
 * Applied on both read and write so a tampered or legacy value can't exceed them.
 */
function applyCaps(entries: string[]): string[] {
  const capped: string[] = [];
  let total = 0;
  for (const entry of entries) {
    if (capped.length >= PROMPT_HISTORY_MAX_ENTRIES) break;
    if (entry.length > PROMPT_HISTORY_MAX_ENTRY_CHARS) continue;
    if (total + entry.length > PROMPT_HISTORY_MAX_TOTAL_CHARS) break;
    capped.push(entry);
    total += entry.length;
  }
  return capped;
}

/** Parse a stored value, tolerating anything: absent, malformed, wrong version, wrong member types. */
function parseStored(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return [];
    const candidate = parsed as Partial<StoredPromptHistory>;
    if (candidate.v !== 1 || !Array.isArray(candidate.entries)) return [];
    const strings = candidate.entries.filter((e): e is string => typeof e === 'string' && e !== '');
    return applyCaps(strings);
  } catch {
    return [];
  }
}

/** Read the history for one surface, newest first. Always safe to call. */
export function loadPromptHistory(key: string, opts: PromptHistoryOptions = {}): string[] {
  const persist = opts.persist ?? true;
  if (!persist || degradedKeys.has(key)) return memoryHistories.get(key) ?? [];

  const storage = getStorage();
  if (!storage) return memoryHistories.get(key) ?? [];
  try {
    const raw = storage.getItem(key);
    // Nothing has ever been persisted under this key, so the mirror — if it has
    // anything — is the better answer. This is the guest-upgrade case: upgrading
    // keeps the same user id and only flips `persist` false → true, and without
    // this the session's recall would go blank at the moment the trial user
    // commits to an account. Safe against resurrection, because clearing deletes
    // the mirror too.
    if (raw === null) return memoryHistories.get(key) ?? [];
    return parseStored(raw);
  } catch {
    return memoryHistories.get(key) ?? [];
  }
}

/**
 * Record a sent prompt at the front of the history.
 *
 * An identical earlier entry is removed rather than duplicated (zsh's
 * `hist_ignore_all_dups`) so the list stays useful. Reads fresh from storage
 * first so a second open tab's entries survive.
 */
export function recordPrompt(key: string, text: string, opts: PromptHistoryOptions = {}): void {
  const trimmed = text.trim();
  if (!trimmed) return;
  if (trimmed.length > PROMPT_HISTORY_MAX_ENTRY_CHARS) return;

  const persist = opts.persist ?? true;
  const existing = loadPromptHistory(key, opts);
  const next = applyCaps([trimmed, ...existing.filter((entry) => entry !== trimmed)]);

  // The mirror is kept current either way, so a later storage failure still has
  // this tab's history to fall back on.
  memoryHistories.set(key, next);
  if (!persist) return;

  // A key that already failed to write stays in the mirror rather than
  // re-serializing up to the full budget and throwing again on every send. A
  // full disk stays full, so retrying per-keystroke buys nothing. The flag is
  // module state, so a page reload retries from scratch, and clearing the
  // history lifts it immediately — those are the two recovery paths.
  if (degradedKeys.has(key)) return;

  const storage = getStorage();
  if (!storage) return;
  try {
    const payload: StoredPromptHistory = { v: 1, entries: next };
    storage.setItem(key, JSON.stringify(payload));
  } catch {
    // Quota exceeded or storage revoked mid-session. The mirror set above keeps
    // recall working for this tab, and the flag makes reads use it rather than
    // the stale persisted value; nothing the user needs to hear about.
    degradedKeys.add(key);
  }
}

/** Forget everything recorded for one surface, in both backends. */
export function clearPromptHistory(key: string, opts: PromptHistoryOptions = {}): void {
  memoryHistories.delete(key);
  degradedKeys.delete(key);
  if (!(opts.persist ?? true)) return;
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // Storage unavailable — the in-memory mirror is already cleared.
  }
}
